import { useCallback } from 'react'

import { useUnit } from 'effector-react'

import InfoIcon from '@/assets/icons/icon-info.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { DIALOG_TYPE } from '@/dialogs/constants'
import { useHasChatUNreadMessages } from '@/models/chattingUI/chats'
import {
  $activeChatDetails,
  markChatAsReadFx,
  markChatAsUNReadFx,
} from '@/models/chattingUI/chats/chats.ts'
import { openDialog } from '@/models/dialogs'
import {
  $hasBlockingPermission,
  $hasHideChatPermission,
} from '@/models/permissions.ts'
import { useHideChat } from '@/modules/chatting/hooks/use-hide-chat.ts'
import { useToggleBlockUser } from '@/modules/chatting/hooks/use-toggle-block-user.ts'
import { useToggleRestrict } from '@/modules/chatting/hooks/use-toggle-restrict'
import MoreIcon from '@/modules/chatting/icons/more.svg'
import { CreatorAvatar } from '@/modules/creators/ui/CreatorAvatar.tsx'

import { useToggleFollow } from '../../hooks/use-toggle-follow'
import { getIsChatRestricted } from '../utils'

export const MoreChatActions = () => {
  const activeChat = useUnit($activeChatDetails)
  const markChatAsReadInProgress = useUnit(markChatAsReadFx.pending)
  const markChatAsUNReadInProgress = useUnit(markChatAsUNReadFx.pending)
  const hasBlockingPermission = useUnit($hasBlockingPermission)
  const hasHideChatPermission = useUnit($hasHideChatPermission)

  const { withUser } = activeChat ?? {}
  const { hideChat, isHideChatLoading } = useHideChat()
  const { unblockUser, isUnblockUserLoading } = useToggleBlockUser()
  const { restrict, unrestrict, isUnrestrictLoading, isRestrictLoading } =
    useToggleRestrict()
  const { followMutation, unfollowMutation } = useToggleFollow()

  const handleOpenProfile = useCallback(() => {
    window.open(`https://onlyfans.com/${withUser?.username}`, '_blank')
  }, [withUser?.username])

  const handleHideChatClick = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.CONFIRMATION,
      title: 'Hide Chat',
      desc: 'Are you sure you want to hide this chat?',
      onConfirm: hideChat,
    })
  }, [hideChat])

  const handleBlockClick = useCallback(() => {
    if (withUser) {
      openDialog({
        type: DIALOG_TYPE.BLOCK_USER,
        user: withUser,
      })
    }
  }, [withUser])

  const handleFollowClick = useCallback(() => {
    if (!activeChat) return

    if (activeChat.withUser.subscribedBy) {
      unfollowMutation.mutate(activeChat.withUser.id)
    } else {
      followMutation.mutate(activeChat.withUser.id)
    }
  }, [followMutation, unfollowMutation, activeChat])
  const hasUnreadMessage = useHasChatUNreadMessages(withUser?.id)
  const handleClickRename = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.RENAME_USER,
      user: withUser,
    })
  }, [withUser])
  const handleOpenManageListsDialog = useCallback(() => {
    openDialog({
      type: DIALOG_TYPE.SAVE_TO_LIST,
    })
  }, [])

  if (!activeChat || !withUser) {
    return null
  }

  const isFollowVisible = withUser.subscribePrice === 0
  const isRestricted = getIsChatRestricted(activeChat)

  return (
    <DropdownMenu>
      <Tooltip
        content="Fan Settings"
        asChild
      >
        <DropdownMenuTrigger className="outline-none">
          <MoreIcon className="h-6 w-4 sm:w-6  hover:text-primary-500" />
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent>
        <div className="flex items-center">
          <div className="relative">
            <CreatorAvatar
              size={24}
              avatarSrc={
                withUser.avatarThumbs?.c50 ??
                withUser.avatarThumbs?.c144 ??
                withUser.avatar
              }
              name={withUser.displayName ?? withUser.name}
            />
            {withUser.online && (
              <div className="bg-success-default absolute bottom-0 right-0.5 h-[5px] w-[5px] rounded-full" />
            )}
          </div>
          <span className="text-text-default ml-2 text-sm font-medium">
            @{withUser.username}
          </span>
        </div>
        <DropdownMenuSeparator className="my-2" />
        <DropdownMenuItem onClick={handleOpenProfile}>
          Open profile in new tab
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleClickRename}>
          Rename User
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleOpenManageListsDialog}>
          Manage Lists
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={markChatAsReadInProgress || markChatAsUNReadInProgress}
          onClick={() => {
            if (hasUnreadMessage) {
              markChatAsReadFx(activeChat.withUser.id)
            } else {
              markChatAsUNReadFx(activeChat.withUser.id)
            }
          }}
        >
          Mark as {hasUnreadMessage ? 'Read' : 'Unread'}
        </DropdownMenuItem>
        {isFollowVisible && (
          <DropdownMenuItem
            disabled={followMutation.isLoading || unfollowMutation.isLoading}
            onClick={handleFollowClick}
          >
            {withUser.subscribedBy ? 'Unfollow' : 'Follow'}
          </DropdownMenuItem>
        )}
        {hasHideChatPermission && (
          <>
            <DropdownMenuSeparator className="my-2" />
            <DropdownMenuItem
              disabled={isHideChatLoading}
              onClick={handleHideChatClick}
              className="group"
            >
              <span className="group-hover:text-danger-default">Hide Chat</span>
            </DropdownMenuItem>
          </>
        )}

        {hasBlockingPermission && (
          <>
            <DropdownMenuSeparator className="my-2" />
            {!isRestricted ? (
              <DropdownMenuItem
                disabled={isRestrictLoading}
                onClick={() => restrict(activeChat.withUser.id)}
                className="group"
              >
                <div className="flex w-full items-center justify-between">
                  <span className="group-hover:text-danger-default">
                    Restrict
                  </span>
                  <Tooltip
                    content={
                      <p className="text-xs font-medium">
                        You will not see messages or comments from this user
                      </p>
                    }
                  >
                    <InfoIcon />
                  </Tooltip>
                </div>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                className="group"
                disabled={isUnrestrictLoading}
                onClick={() => unrestrict(activeChat.withUser.id)}
              >
                <span className="group-hover:text-danger-default">
                  Unrestrict
                </span>
              </DropdownMenuItem>
            )}
            {!withUser.isBlocked ? (
              <DropdownMenuItem
                className="group"
                onClick={handleBlockClick}
              >
                <span className="group-hover:text-danger-default">Block</span>
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                disabled={isUnblockUserLoading}
                className="group"
                onClick={() => unblockUser(activeChat.withUser.id)}
              >
                <span className="group-hover:text-danger-default">Unblock</span>
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
